import { useCallback, useState } from "react";
import { debounce } from "lodash";
import { getData } from "../api/ApiServices";

interface DropdownOption {
  value: string;
  label: string;
  [key: string]: any;
}

export const useSearchDropdowns = () => {
  const [items, setItems] = useState<DropdownOption[]>([]);
  const [racks, setRacks] = useState<DropdownOption[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isLoadingRacks, setIsLoadingRacks] = useState(false);

  const loadItems = useCallback(async () => {
    setIsLoadingItems(true);
    try {
      const response: any = await getData("/items", {
        filter: { status: "ACTIVE" },
        limit: 100
      });
      
      if (response?.data?.data) {
        const options = response.data.data.map((item: any) => ({
          value: item._id,
          label: item.internalPartNo || "No Part Number",
          partName: item.partName,
          itemData: item
        }));
        setItems(options);
      }
    } catch (error) {
      console.error("Error loading items:", error);
    } finally {
      setIsLoadingItems(false);
    }
  }, []);


  const loadRacks = useCallback(async () => {
    setIsLoadingRacks(true);
    try {
      const response: any = await getData("/racks", {
        filter: { status: "ACTIVE" },
        limit: 100
      });
      
      if (response?.data?.data) {
        const options = response.data.data.map((rack: any) => ({
          value: rack._id,
          label: rack.rackName
        }));
        setRacks(options);
      }
    } catch (error) {
      console.error("Error loading racks:", error);
    } finally {
      setIsLoadingRacks(false);
    }
  }, []);


  const searchItems = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm && items.length > 0) return;
      
      setIsLoadingItems(true);
      try {
        const response: any = await getData("/items", {
          search: searchTerm,
          filter: { status: "ACTIVE" },
          limit: 100
        });
        
        if (response?.data?.data) {
          const itemOptions = response.data.data.map((item: any) => ({
            value: item._id,
            label: item.internalPartNo || "No Part Number",
            partName: item.partName,
            itemData: item
          }));
          setItems(itemOptions);
        }
      } catch (error) {
        console.error("Error fetching items:", error);
      } finally {
        setIsLoadingItems(false);
      }
    }, 300),
    [items.length]
  );

  const searchRacks = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm && racks.length > 0) return;
      
      setIsLoadingRacks(true);
      try {
        const response: any = await getData("/racks", {
          search: searchTerm,
          filter: { status: "ACTIVE" },
          limit: 100
        });
        
        if (response?.data?.data) {
          const rackOptions = response.data.data.map((rack: any) => ({
            value: rack._id,
            label: rack.rackName
          }));
          setRacks(rackOptions);
        }
      } catch (error) {
        console.error("Error fetching racks:", error);
      } finally {
        setIsLoadingRacks(false);
      }
    }, 300),
    [racks.length]
  );

  return {
    items,
    racks,
    loadItems,  
    loadRacks,  
    searchItems,
    searchRacks,
    isLoadingItems,
    isLoadingRacks,
    setItems,
    setRacks
  };
};